import {
  ClinicOrder,
  ClinicOrderQuery,
  ClinicOrderStatus,
  CreateOrderRequest,
  ListResult,
  PayRequest,
  ResendReceiptResult,
} from '../types';
import { USE_MOCK_ORDERS } from '../constants/buyer-portal';
import { orderMock } from '../mocks/orderMock';
import { isInNativeApp, nativeBridge } from '../bridge';
import type { DownloadFileResponsePayload } from '../bridge/types';
import { clinicHttp } from './http';
import { ENDPOINTS } from './endpoints';
import {
  mapOrderBuyerDto,
  OrderBuyerDtoBackend,
  mapGuestLookupDto,
  GuestOrderLookupDtoBackend,
} from './orderMapper';
import { CartDtoBackend } from './cartMapper';

export type OrderService = {
  create: (req: CreateOrderRequest) => Promise<ClinicOrder>;
  pay: (orderId: string, req: PayRequest) => Promise<ClinicOrder>;
  simulatePayFailed: (orderId: string) => Promise<ClinicOrder>;
  cancel: (orderId: string) => Promise<ClinicOrder>;
  payGuest: (publicOrderNo: string, email: string, req: PayRequest) => Promise<ClinicOrder>;
  cancelGuest: (publicOrderNo: string, email: string) => Promise<ClinicOrder>;
  list: (query?: ClinicOrderQuery) => Promise<ListResult<ClinicOrder>>;
  detail: (orderId: string) => Promise<ClinicOrder>;
  confirmReceived: (orderId: string) => Promise<ClinicOrder>;
  reorder: (
    orderId: string,
  ) => Promise<{ addedCount: number; skipped: { productId: string; reason: string }[] }>;
  lookup: (publicOrderNo: string, email: string) => Promise<ClinicOrder>;
  resendReceipt: (orderId: string) => Promise<ResendReceiptResult>;
  /** Trigger a browser download of the invoice PDF for an order. */
  downloadInvoicePdf: (orderId: string, orderNumber?: string) => Promise<void>;
};

type BackendListResult = {
  items: OrderBuyerDtoBackend[] | null;
  skip: number;
  take: number;
  total: number;
};

type ReorderBackend = {
  addedCount: number;
  skippedCount: number;
  skippedReasons?: { productId: string; productName?: string | null; reason?: string | null }[] | null;
};

const realOrderService: OrderService = {
  create: async (req) => {
    // Guest checkout path — the buyer endpoint requires auth, so guests must
    // hit /public/orders. We detect guest by presence of guestContact.
    if (req.guestContact) {
      const items = (req.guestItems ?? []).map((g) => ({
        productId: g.productId,
        qty: g.quantity,
      }));
      const contactName = `${req.guestContact.firstName ?? ''} ${req.guestContact.lastName ?? ''}`
        .trim();
      const payload = {
        email: req.guestContact.email,
        phone: req.guestContact.phone,
        contactName,
        shippingAddress: req.shippingAddress,
        // Always send a billing snapshot. Checkout copies shipping into
        // billing when the user picked "same as shipping" — backend never
        // sees a null billing sentinel from us.
        billingAddress: req.billingAddress ?? req.shippingAddress,
        items,
      };
      // Backend returns GuestOrderCreatedDto { publicOrderNo, status, reservedUntil, total }
      // — not the full OrderBuyerDto. Re-fetch via lookup so the rest of the
      // app receives a complete ClinicOrder.
      const createRes = await clinicHttp.post<{
        publicOrderNo: string;
        status: ClinicOrderStatus;
        reservedUntil?: string | null;
        total: number;
      }>(ENDPOINTS.PUBLIC.ORDERS, payload);
      return realOrderService.lookup(createRes.data.publicOrderNo, req.guestContact.email);
    }

    // Authed "Buy now" path — order built from explicit items, bypassing the
    // server-side cart so the user's cart stays intact.
    let items: { productId: string; qty: number }[];
    if (req.buyNowItems && req.buyNowItems.length > 0) {
      items = req.buyNowItems.map((i) => ({
        productId: i.productId,
        qty: i.quantity,
      }));
    } else {
      // Default authed path — items pulled from the server-side cart.
      const cart = await clinicHttp.get<CartDtoBackend>(ENDPOINTS.BUYER.CART);
      items = (cart.data.items ?? []).map((i) => ({
        productId: i.productId,
        qty: i.quantity,
      }));
    }
    const payload = {
      items,
      shippingAddress: req.shippingAddress,
      // See comment above on the guest payload.
      billingAddress: req.billingAddress ?? req.shippingAddress,
    };
    const res = await clinicHttp.post<OrderBuyerDtoBackend>(
      ENDPOINTS.BUYER.ORDERS,
      payload,
    );
    return mapOrderBuyerDto(res.data);
  },
  pay: async (orderId, req) => {
    const res = await clinicHttp.post<OrderBuyerDtoBackend>(
      ENDPOINTS.BUYER.ORDER_PAY(orderId),
      { sourceId: req.sourceId },
    );
    return mapOrderBuyerDto(res.data);
  },
  simulatePayFailed: async (orderId) => {
    const res = await clinicHttp.post<OrderBuyerDtoBackend>(
      ENDPOINTS.BUYER.ORDER_SIMULATE_PAY_FAILED(orderId),
    );
    return mapOrderBuyerDto(res.data);
  },
  cancel: async (orderId) => {
    const res = await clinicHttp.post<OrderBuyerDtoBackend>(
      ENDPOINTS.BUYER.ORDER_CANCEL(orderId),
    );
    return mapOrderBuyerDto(res.data);
  },
  payGuest: async (publicOrderNo, email, req) => {
    const res = await clinicHttp.post<GuestOrderLookupDtoBackend>(
      ENDPOINTS.PUBLIC.ORDER_PAY(publicOrderNo),
      { email, sourceId: req.sourceId },
    );
    return mapGuestLookupDto(res.data, email);
  },
  cancelGuest: async (publicOrderNo, email) => {
    const res = await clinicHttp.post<GuestOrderLookupDtoBackend>(
      ENDPOINTS.PUBLIC.ORDER_CANCEL(publicOrderNo),
      { email },
    );
    return mapGuestLookupDto(res.data, email);
  },
  list: async (query = {}) => {
    const res = await clinicHttp.get<BackendListResult>(ENDPOINTS.BUYER.ORDERS, {
      params: query,
    });
    return {
      items: (res.data.items ?? []).map(mapOrderBuyerDto),
      skip: res.data.skip,
      take: res.data.take,
      total: res.data.total,
    };
  },
  detail: async (orderId) => {
    const res = await clinicHttp.get<OrderBuyerDtoBackend>(
      ENDPOINTS.BUYER.ORDER_DETAIL(orderId),
    );
    return mapOrderBuyerDto(res.data);
  },
  confirmReceived: async (orderId) => {
    const res = await clinicHttp.post<OrderBuyerDtoBackend>(
      ENDPOINTS.BUYER.ORDER_CONFIRM_RECEIVED(orderId),
    );
    return mapOrderBuyerDto(res.data);
  },
  reorder: async (orderId) => {
    const res = await clinicHttp.post<ReorderBackend>(
      ENDPOINTS.BUYER.ORDER_REORDER(orderId),
    );
    return {
      addedCount: res.data.addedCount,
      skipped: (res.data.skippedReasons ?? []).map((s) => ({
        productId: s.productId,
        reason: s.reason ?? '',
      })),
    };
  },
  lookup: async (publicOrderNo, email) => {
    const res = await clinicHttp.post<GuestOrderLookupDtoBackend>(
      ENDPOINTS.PUBLIC.ORDER_LOOKUP,
      { publicOrderNo, email },
    );
    return mapGuestLookupDto(res.data, email);
  },
  resendReceipt: async () => {
    throw new Error('Resend receipt endpoint not available on backend.');
  },
  downloadInvoicePdf: async (orderId, orderNumber) => {
    // PDF endpoint requires the bearer token, so we can't `window.open` it
    // directly — fetch as blob via the authed axios client. Force
    // `Accept: application/pdf` so the backend's content-negotiation can't
    // fall back to the legacy JSON `/invoice` shape.
    const res = await clinicHttp.get<Blob>(ENDPOINTS.BUYER.INVOICE_PDF(orderId), {
      responseType: 'blob',
      headers: { Accept: 'application/pdf' },
    });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const filename = `invoice-${orderNumber || orderId}.pdf`;

    if (isInNativeApp()) {
      // iOS WKWebView ignores `<a download>` and navigates to the blob: URL,
      // stranding the user. Hand the bytes to native via DOWNLOAD_FILE bridge
      // message — native decodes base64 and triggers a system share sheet.
      // Pre-check size: native hard-caps at 10MB and the base64 payload itself
      // is ~33% larger, so checking on the raw blob avoids wasting CPU on an
      // encode that native will reject anyway.
      if (blob.size > MAX_NATIVE_DOWNLOAD_BYTES) {
        throw new Error(
          'Invoice file too large to share in app. Please download in a web browser.',
        );
      }
      const base64 = await blobToBase64(blob);
      const response = await nativeBridge.request<DownloadFileResponsePayload>(
        {
          type: 'DOWNLOAD_FILE',
          payload: {
            filename,
            mimeType: 'application/pdf',
            base64,
            sizeBytes: blob.size,
          },
        },
        NATIVE_DOWNLOAD_TIMEOUT_MS,
      );
      // Branch only on `success`. `cancelled` is reserved in the protocol but
      // unreliable today — expo-sharing resolves cancel and save identically
      // on iOS, so the native side currently can't set it.
      if (!response?.success) {
        throw new Error(response?.errorMessage ?? 'Native download failed.');
      }
      return;
    }

    // Browser path: re-wrap as application/pdf and trigger an anchor download.
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

const MAX_NATIVE_DOWNLOAD_BYTES = 10 * 1024 * 1024;
const NATIVE_DOWNLOAD_TIMEOUT_MS = 30000;

const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      // dataUrl is `data:<mime>;base64,<payload>` — strip the prefix.
      const comma = dataUrl.indexOf(',');
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsDataURL(blob);
  });

export const orderService: OrderService = USE_MOCK_ORDERS ? orderMock : realOrderService;
